// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from './main.ts';
import type { Seat } from './data.ts';
import { STATE_NAMES } from './data.ts';
import { GROUP_META, partyColor } from './parties.ts';
import { fmtInt, fmtPct, fmtSigned, esc, escTip, fullName } from './utils/format.ts';

let overlay: HTMLDivElement | null = null;
let drawer: HTMLDivElement | null = null;

function teardown(onClose?: () => void): void {
  if (drawer) { drawer.classList.remove('visible'); }
  if (overlay) { overlay.classList.remove('visible'); }
  const ov = overlay, dr = drawer;
  overlay = null; drawer = null;
  setTimeout(() => { ov?.remove(); dr?.remove(); }, 260);
  onClose?.();
}

/** Close any open seat drawer without touching the hash (used on view navigation). */
export function closeSeatDrawer(): void {
  if (!drawer && !overlay) return;
  teardown();
}

export function openSeatDrawer(seat: Seat, ctx: Ctx, onClose?: () => void): void {
  // Detach any prior instance from the DOM entirely (iOS off-canvas scroll fix).
  overlay?.remove(); drawer?.remove();

  overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);

  drawer = document.createElement('div');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', `Result for ${seat.name}`);

  const win = seat.tcp[0];
  const run = seat.tcp[1];
  const held = seat.member.group;
  const cands = seat.candidates.filter((c) => c.votes > 0);
  const maxFp = Math.max(...cands.map((c) => c.totalPct), 1);

  drawer.innerHTML = `
    <div class="drawer-head" style="background:${GROUP_META[held].color}">
      <button class="close" aria-label="Close">×</button>
      <h3>${esc(seat.name)}</h3>
      <div class="st">${esc(STATE_NAMES[seat.state] || seat.state)} · won by ${esc(GROUP_META[held].label)}${seat.changedHands ? ' · gain' : ''}${seat.nonClassic ? ' · non-classic' : ''}</div>
    </div>
    <div class="drawer-body">
      <div class="kv"><span class="k">Member elected</span><span class="v">${esc(fullName(seat.member.given, seat.member.surname))}</span></div>
      <div class="kv"><span class="k">Party</span><span class="v">${esc(seat.member.partyName)}</span></div>
      ${seat.changedHands ? `<div class="kv"><span class="k">Previously held by</span><span class="v">${esc(seat.prevParty || '—')}</span></div>` : ''}

      <h4>Two-candidate result</h4>
      <div class="cand" style="grid-template-columns:1fr auto">
        <span class="nm"><span class="swatch" style="display:inline-block;background:${partyColor(win.party, win.group)}"></span> ${esc(fullName(win.given, win.surname))} <span style="color:var(--text-tertiary)">${esc(win.party)}</span></span>
        <span class="pc">${fmtPct(win.pct)}</span>
        <span class="bar"><div style="width:${win.pct}%;background:${partyColor(win.party, win.group)}"></div></span>
      </div>
      <div class="cand" style="grid-template-columns:1fr auto">
        <span class="nm"><span class="swatch" style="display:inline-block;background:${partyColor(run.party, run.group)}"></span> ${esc(fullName(run.given, run.surname))} <span style="color:var(--text-tertiary)">${esc(run.party)}</span></span>
        <span class="pc">${fmtPct(run.pct)}</span>
        <span class="bar"><div style="width:${run.pct}%;background:${partyColor(run.party, run.group)}"></div></span>
      </div>
      <div class="kv"><span class="k">Winning margin</span><span class="v">${seat.marginPct.toFixed(seat.marginPct < 0.1 ? 2 : 1)}% · ${fmtInt(Math.abs(win.votes - run.votes))} votes</span></div>

      <h4>Two-party-preferred (Labor v Coalition)</h4>
      <div class="compbar" style="height:34px">
        <div style="background:var(--alp);width:${seat.tppALPPct}%">${fmtPct(seat.tppALPPct)}</div>
        <div style="background:var(--coa);width:${seat.tppLNPPct}%">${fmtPct(seat.tppLNPPct)}</div>
      </div>
      <div class="kv"><span class="k">Swing to Labor</span><span class="v" style="color:${seat.tppSwingToALP == null ? 'var(--text-tertiary)' : seat.tppSwingToALP >= 0 ? 'var(--alp)' : 'var(--coa)'}">${seat.tppSwingToALP == null ? 'n/a' : fmtSigned(seat.tppSwingToALP)}</span></div>
      ${seat.tppSwingToALP == null ? `<p class="note">This seat was non-classic in 2022, so there is no comparable two-party figure to measure a swing against.</p>` : ''}
      ${seat.nonClassic ? `<p class="note">This is a non-classic seat: the final two were not Labor v Coalition, so the two-party figure above is the AEC's notional calculation, not what decided the seat.</p>` : ''}

      <h4>First preferences — all candidates</h4>
      ${cands.map((c) => `
        <div class="cand" style="grid-template-columns:1fr auto">
          <span class="nm"><span class="swatch" style="display:inline-block;background:${partyColor(c.party, c.group)}"></span> ${esc(fullName(c.given, c.surname))} <span style="color:var(--text-tertiary)">${esc(c.party || 'IND')}</span>${c.elected ? ' ✓' : ''}</span>
          <span class="pc">${fmtPct(c.totalPct)}</span>
          <span class="bar"><div style="width:${(c.totalPct / maxFp) * 100}%;background:${partyColor(c.party, c.group)}" data-tip="${escTip(`${fullName(c.given, c.surname)}\n${fmtInt(c.votes)} first-preference votes`)}"></div></span>
        </div>`).join('')}

      <h4>Participation</h4>
      <div class="kv"><span class="k">Enrolment</span><span class="v">${fmtInt(seat.enrolment)}</span></div>
      <div class="kv"><span class="k">Turnout</span><span class="v">${fmtPct(seat.turnoutPct)}</span></div>
      <div class="kv"><span class="k">Informal vote</span><span class="v">${fmtPct(seat.informalPct)} (${fmtSigned(seat.informalSwing)})</span></div>
    </div>`;

  document.body.appendChild(drawer);
  // force reflow then animate in
  void drawer.offsetWidth;
  overlay.classList.add('visible');
  drawer.classList.add('visible');

  const close = () => teardown(onClose);
  overlay.addEventListener('click', close);
  drawer.querySelector('.close')!.addEventListener('click', close);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);
  // keep ctx referenced (available for future cross-links)
  void ctx;
}
